export const getCabinetQuotes = async (params = {}) => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      searchParams.set(key, value);
    }
  });
  const response = await fetch(`/api/cabinet-quotes/quotes?${searchParams.toString()}`);
  return response.json();
};

export const createCabinetQuote = async (payload) => {
  const userId = payload?.userId || "";
  const response = await fetch(`/api/cabinet-quotes/quotes?userId=${encodeURIComponent(userId)}`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data?.error || "Failed to create cabinet quote");
  }
  return data;
};

export const getCabinetQuoteDetail = async (quoteId, userId) => {
  const response = await fetch(`/api/cabinet-quotes/quotes/${quoteId}?userId=${userId || ""}`);
  return response.json();
};

export const updateCabinetQuote = async (quoteId, userId, payload) => {
  const endpoint = `/api/cabinet-quotes/quotes/${quoteId}?userId=${encodeURIComponent(userId || "")}`;
  const methods = ["PATCH", "PUT"];
  let lastError = null;

  for (const method of methods) {
    const response = await fetch(endpoint, {
      method,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    let data = null;
    try {
      data = await response.json();
    } catch (parseError) {
      data = null;
    }

    if (response.ok) {
      return data;
    }

    if (![404, 405].includes(response.status)) {
      throw new Error(data?.error || "Failed to update cabinet quote");
    }

    lastError = data?.error || `Method ${method} not supported`;
  }

  throw new Error(lastError || "Failed to update cabinet quote");
};

export const deleteCabinetQuote = async (quoteId, userId) => {
  const response = await fetch(`/api/cabinet-quotes/quotes/${quoteId}?userId=${encodeURIComponent(userId || "")}`, {
    method: "DELETE",
    headers: {
      Accept: "application/json",
    },
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data?.error || "Failed to delete cabinet quote");
  }
  return data;
};

export const getCabinetEmployees = async (userId, search = "") => {
  const response = await fetch(`/api/cabinet-quotes/employees?userId=${encodeURIComponent(userId || "")}&search=${encodeURIComponent(search)}`);
  return response.json();
};

export const getQuoteCollaborators = async (quoteId, userId) => {
  const response = await fetch(`/api/cabinet-quotes/quotes/${quoteId}/collaborators?userId=${encodeURIComponent(userId || "")}`);
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data?.error || "Failed to load collaborators");
  }
  return data;
};

export const addQuoteCollaborator = async (quoteId, userId, payload) => {
  const response = await fetch(`/api/cabinet-quotes/quotes/${quoteId}/collaborators?userId=${encodeURIComponent(userId || "")}`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data?.error || "Failed to add collaborator");
  }
  return data;
};

export const updateQuoteCollaboratorPermission = async (quoteId, actorUserId, collaboratorUserId, permission) => {
  const response = await fetch(
    `/api/cabinet-quotes/quotes/${quoteId}/collaborators/${encodeURIComponent(collaboratorUserId)}?userId=${encodeURIComponent(actorUserId || "")}`,
    {
      method: "PATCH",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ permission }),
    }
  );
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data?.error || "Failed to update collaborator permission");
  }
  return data;
};

export const removeQuoteCollaborator = async (quoteId, actorUserId, collaboratorUserId) => {
  const response = await fetch(
    `/api/cabinet-quotes/quotes/${quoteId}/collaborators/${encodeURIComponent(collaboratorUserId)}?userId=${encodeURIComponent(actorUserId || "")}`,
    {
      method: "DELETE",
      headers: {
        Accept: "application/json",
      },
    }
  );
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data?.error || "Failed to remove collaborator");
  }
  return data;
};

export const getCabinetAdminContext = async (userId, roles = []) => {
  const roleString = Array.isArray(roles) ? roles.join(",") : "";
  const response = await fetch(`/api/cabinet-quotes/admin/context?userId=${encodeURIComponent(userId || "")}&roles=${encodeURIComponent(roleString)}`);
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data?.error || "Failed to load cabinet admin context");
  }
  return data;
};

export const getCabinetFormTemplateConfig = async (userId) => {
  const response = await fetch(`/api/cabinet-quotes/config/form?userId=${encodeURIComponent(userId || "")}`);
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data?.error || "Failed to load cabinet form templates");
  }
  return data;
};

export const updateCabinetFormTemplate = async (userId, payload, roles = []) => {
  const roleString = Array.isArray(roles) ? roles.join(",") : "";
  const response = await fetch(`/api/cabinet-quotes/config/form?userId=${encodeURIComponent(userId || "")}&roles=${encodeURIComponent(roleString)}`, {
    method: "PUT",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data?.error || "Failed to save cabinet form template");
  }
  return data;
};

export const getQuoteChecklistTemplates = async (quoteId, userId) => {
  const response = await fetch(`/api/cabinet-quotes/quotes/${quoteId}/checklist/templates?userId=${encodeURIComponent(userId || "")}`);
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data?.error || "Failed to load checklist templates");
  }
  return data;
};

export const createQuoteChecklistTemplate = async (quoteId, userId, payload) => {
  const response = await fetch(`/api/cabinet-quotes/quotes/${quoteId}/checklist/templates?userId=${encodeURIComponent(userId || "")}`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data?.error || "Failed to create checklist template");
  }
  return data;
};

export const updateQuoteChecklistTemplate = async (quoteId, userId, templateId, payload) => {
  const response = await fetch(
    `/api/cabinet-quotes/quotes/${quoteId}/checklist/templates/${encodeURIComponent(templateId)}?userId=${encodeURIComponent(userId || "")}`,
    {
      method: "PATCH",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    }
  );
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data?.error || "Failed to update checklist template");
  }
  return data;
};

export const applyQuoteChecklistTemplate = async (quoteId, userId, templateId) => {
  const response = await fetch(
    `/api/cabinet-quotes/quotes/${quoteId}/checklist/templates/${encodeURIComponent(templateId)}/apply?userId=${encodeURIComponent(userId || "")}`,
    {
      method: "POST",
      headers: {
        Accept: "application/json",
      },
    }
  );
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data?.error || "Failed to apply checklist template");
  }
  return data;
};

export const updateQuoteChecklistItem = async (quoteId, userId, itemId, payload) => {
  const response = await fetch(
    `/api/cabinet-quotes/quotes/${quoteId}/checklist/${encodeURIComponent(itemId)}?userId=${encodeURIComponent(userId || "")}`,
    {
      method: "PATCH",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    }
  );
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data?.error || "Failed to update checklist item");
  }
  return data;
};

export const getQuoteChatMessages = async (quoteId, userId, channel) => {
  const response = await fetch(
    `/api/cabinet-quotes/quotes/${quoteId}/chat/${encodeURIComponent(channel)}?userId=${encodeURIComponent(userId || "")}`
  );
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data?.error || "Failed to load chat messages");
  }
  return Array.isArray(data) ? data : [];
};

export const sendQuoteChatMessage = async (quoteId, userId, channel, payload) => {
  const formData = new FormData();
  formData.append("text", String(payload?.text || ""));
  formData.append("user_name", String(payload?.user_name || ""));
  (Array.isArray(payload?.attachments) ? payload.attachments : []).forEach((file) => {
    formData.append("attachments", file);
  });

  const response = await fetch(
    `/api/cabinet-quotes/quotes/${quoteId}/chat/${encodeURIComponent(channel)}?userId=${encodeURIComponent(userId || "")}`,
    {
      method: "POST",
      headers: {
        Accept: "application/json",
      },
      body: formData,
    }
  );
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data?.error || "Failed to send chat message");
  }
  return data;
};

export const createQuoteRevision = async (quoteId, userId, payload) => {
  const response = await fetch(`/api/cabinet-quotes/quotes/${quoteId}/revisions?userId=${encodeURIComponent(userId || "")}`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data?.error || "Failed to create quote revision");
  }
  return data;
};


export const generateCabinetCustomerLink = async (quoteId, userId) => {
  const response = await fetch(`/api/cabinet-quotes/quotes/${quoteId}/customer-link?userId=${encodeURIComponent(userId || "")}`, {
    method: "POST",
    headers: {
      Accept: "application/json",
    },
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data?.error || "Failed to generate customer link");
  }
  return data;
};

export const renderCabinetDesign = async (userId, file, { prompt = "", size = "auto", mode = "enhance" } = {}) => {
  const formData = new FormData();
  formData.append("design", file);
  formData.append("prompt", prompt);
  formData.append("size", size);
  formData.append("mode", mode);

  const response = await fetch(`/api/cabinet-quotes/ai/render?userId=${encodeURIComponent(userId || "")}`, {
    method: "POST",
    headers: {
      Accept: "application/json",
    },
    body: formData,
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data?.error || "Failed to render design");
  }
  return data;
};

export const getCabinetCustomerPortal = async (token) => {
  const response = await fetch(`/api/cabinet-quotes/customer/${encodeURIComponent(token)}`);
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data?.error || "Failed to load customer quote portal");
  }
  return data;
};

export const sendCabinetCustomerPortalChat = async (token, payload) => {
  const formData = new FormData();
  formData.append("text", String(payload?.text || ""));
  formData.append("user_name", String(payload?.user_name || ""));
  (Array.isArray(payload?.attachments) ? payload.attachments : []).forEach((file) => {
    formData.append("attachments", file);
  });

  const response = await fetch(`/api/cabinet-quotes/customer/${encodeURIComponent(token)}/chat`, {
    method: "POST",
    headers: {
      Accept: "application/json",
    },
    body: formData,
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data?.error || "Failed to send customer chat message");
  }
  return data;
};
